// Pagina Impostazioni Privacy - GDPR Compliant
// Permette agli utenti di gestire i propri dati e diritti GDPR

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { 
  Shield, 
  Download, 
  Trash2, 
  Cookie, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface ExportStatus {
  has_request: boolean;
  status?: string;
  requested_at?: string;
}

interface DeletionStatus {
  has_request: boolean;
  status?: string;
  requested_at?: string;
  admin_notes?: string;
}

interface UserConsents {
  terms_accepted?: boolean;
  terms_accepted_at?: string;
  marketing_accepted?: boolean;
  marketing_accepted_at?: string;
}

const PrivacySettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [userConsents, setUserConsents] = useState<UserConsents>({});
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [loading, setLoading] = useState({
    cookies: false,
    export: false,
    deletion: false,
    marketing: false
  });

  useEffect(() => {
    loadCookiePreferences();
    loadUserConsents();
    loadExportStatus();
    loadDeletionStatus();
  }, []);

  const loadUserConsents = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/user/consents`);
      setUserConsents(response.data);
    } catch (error) {
      console.error('Errore caricamento consensi:', error);
    }
  };

  const updateMarketingConsent = async (accepted: boolean) => {
    setLoading({ ...loading, marketing: true });
    try {
      await axios.put(`${API}/gdpr/user/consents?marketing_accepted=${accepted}`);
      setUserConsents({ ...userConsents, marketing_accepted: accepted });
      toast.success(accepted ? 'Consenso marketing attivato' : 'Consenso marketing revocato');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setLoading({ ...loading, marketing: false });
    }
  };

  const loadCookiePreferences = async () => {
    try {
      const sessionId = localStorage.getItem('gdpr_session_id');
      if (sessionId) {
        const response = await axios.get(`${API}/gdpr/consent/cookies/${sessionId}`);
        if (response.data.has_consented) {
          setCookiePreferences(response.data.preferences);
        }
      }
    } catch (error) {
      console.error('Errore caricamento preferenze cookie:', error);
    }
  };

  const loadExportStatus = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/data-export/status`);
      setExportStatus(response.data);
    } catch (error) {
      console.error('Errore caricamento stato export:', error);
    }
  };

  const loadDeletionStatus = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/data-deletion/status`);
      setDeletionStatus(response.data);
    } catch (error) {
      console.error('Errore caricamento stato cancellazione:', error);
    }
  };

  const saveCookiePreferences = async () => {
    setLoading({ ...loading, cookies: true });
    try {
      const sessionId = localStorage.getItem('gdpr_session_id') || 'sess_' + Date.now();
      await axios.post(`${API}/gdpr/consent/cookies`, {
        session_id: sessionId,
        preferences: cookiePreferences,
        user_agent: navigator.userAgent
      });
      localStorage.setItem('cookie_consent', JSON.stringify(cookiePreferences));
      toast.success('Preferenze cookie aggiornate');
    } catch (error) {
      toast.error('Errore nel salvataggio delle preferenze');
    } finally {
      setLoading({ ...loading, cookies: false });
    }
  };

  const requestDataExport = async () => {
    setLoading({ ...loading, export: true });
    try {
      await axios.post(`${API}/gdpr/data-export/request`);
      toast.success('Richiesta di export inviata');
      loadExportStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nella richiesta');
    } finally {
      setLoading({ ...loading, export: false });
    }
  };

  const downloadExportedData = async () => {
    setLoading({ ...loading, export: true });
    try {
      const response = await axios.get(`${API}/gdpr/data-export/download`);
      
      // Crea e scarica il file JSON
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `i_miei_dati_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download completato');
    } catch (error) {
      toast.error('Errore nel download');
    } finally {
      setLoading({ ...loading, export: false });
    }
  };

  const requestDataDeletion = async () => {
    setLoading({ ...loading, deletion: true });
    try {
      await axios.post(`${API}/gdpr/data-deletion/request?reason=${encodeURIComponent(deleteReason)}`);
      toast.success('Richiesta di cancellazione inviata');
      setShowDeleteDialog(false);
      loadDeletionStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nella richiesta');
    } finally {
      setLoading({ ...loading, deletion: false });
    }
  };

  const cancelDeletionRequest = async () => {
    setLoading({ ...loading, deletion: true });
    try {
      await axios.delete(`${API}/gdpr/data-deletion/cancel`);
      toast.success('Richiesta di cancellazione annullata');
      loadDeletionStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setLoading({ ...loading, deletion: false });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 text-yellow-600 bg-yellow-100 px-2 py-1 rounded text-sm">
            <Clock className="h-4 w-4" />
            In attesa
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded text-sm">
            <CheckCircle2 className="h-4 w-4" />
            Completato
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-red-600 bg-red-100 px-2 py-1 rounded text-sm">
            <XCircle className="h-4 w-4" />
            Rifiutato
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="privacy-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          Impostazioni Privacy
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestisci i tuoi dati personali e le preferenze privacy
        </p>
      </div>

      {/* Preferenze Cookie */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cookie className="h-5 w-5" />
            Preferenze Cookie
          </CardTitle>
          <CardDescription>
            Gestisci quali cookie possono essere utilizzati durante la tua navigazione
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cookie necessari */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium">Cookie Necessari</p>
              <p className="text-sm text-muted-foreground">
                Essenziali per il funzionamento. Sempre attivi.
              </p>
            </div>
            <Switch checked={true} disabled />
          </div>

          {/* Cookie analitici */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="font-medium">Cookie Analitici</p>
              <p className="text-sm text-muted-foreground">
                Ci aiutano a migliorare il servizio
              </p>
            </div>
            <Switch
              checked={cookiePreferences.analytics}
              onCheckedChange={(checked) => setCookiePreferences({ ...cookiePreferences, analytics: checked })}
              data-testid="analytics-cookie-toggle"
            />
          </div>

          {/* Cookie marketing */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="font-medium">Cookie di Marketing</p>
              <p className="text-sm text-muted-foreground">
                Per contenuti personalizzati
              </p>
            </div>
            <Switch
              checked={cookiePreferences.marketing}
              onCheckedChange={(checked) => setCookiePreferences({ ...cookiePreferences, marketing: checked })}
              data-testid="marketing-cookie-toggle"
            />
          </div>

          <Button onClick={saveCookiePreferences} disabled={loading.cookies}>
            {loading.cookies && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salva Preferenze
          </Button>
        </CardContent>
      </Card>

      {/* Consenso Marketing Account */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Comunicazioni Marketing
          </CardTitle>
          <CardDescription>
            Gestisci il consenso per ricevere comunicazioni commerciali via email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <p className="font-medium">Email Promozionali</p>
              <p className="text-sm text-muted-foreground">
                Ricevi offerte, novità e comunicazioni commerciali
              </p>
              {userConsents.marketing_accepted_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Consenso dato il: {new Date(userConsents.marketing_accepted_at).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
            <Switch
              checked={userConsents.marketing_accepted || false}
              onCheckedChange={updateMarketingConsent}
              disabled={loading.marketing}
              data-testid="account-marketing-toggle"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Puoi revocare il consenso in qualsiasi momento. Non ti invieremo più comunicazioni promozionali.
          </p>
        </CardContent>
      </Card>

      {/* Export Dati */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Esporta i Tuoi Dati
          </CardTitle>
          <CardDescription>
            Ai sensi dell'Art. 15 del GDPR, hai diritto di ottenere una copia dei tuoi dati personali
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {exportStatus?.has_request && exportStatus.status === 'pending' ? (
            <div className="flex items-center gap-3">
              {getStatusBadge('pending')}
              <span className="text-sm text-muted-foreground">
                Richiesta del {new Date(exportStatus.requested_at!).toLocaleDateString('it-IT')}
              </span>
            </div>
          ) : null}

          <div className="flex gap-2">
            <Button
              onClick={downloadExportedData}
              disabled={loading.export}
              data-testid="export-data-btn"
            >
              {loading.export && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Download className="h-4 w-4 mr-2" />
              Scarica i Miei Dati
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            I dati verranno esportati in formato JSON e includeranno: profilo, ordini, messaggi e consensi.
          </p>
        </CardContent>
      </Card>

      {/* Cancellazione Account */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Cancellazione Account
          </CardTitle>
          <CardDescription>
            Ai sensi dell'Art. 17 del GDPR (Diritto all'oblio), puoi richiedere la cancellazione dei tuoi dati
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {deletionStatus?.has_request ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {getStatusBadge(deletionStatus.status!)}
                <span className="text-sm text-muted-foreground">
                  Richiesta del {new Date(deletionStatus.requested_at!).toLocaleDateString('it-IT')}
                </span>
              </div>

              {deletionStatus.status === 'rejected' && deletionStatus.admin_notes && (
                <div className="p-3 bg-destructive/10 rounded-lg text-sm">
                  <p className="font-medium text-destructive">Motivo del rifiuto:</p>
                  <p className="text-muted-foreground">{deletionStatus.admin_notes}</p>
                </div>
              )}

              {deletionStatus.status === 'pending' && (
                <Button
                  variant="outline"
                  onClick={cancelDeletionRequest}
                  disabled={loading.deletion}
                >
                  {loading.deletion && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Annulla Richiesta
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="p-4 bg-destructive/10 rounded-lg flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">Attenzione</p>
                  <p className="text-muted-foreground">
                    La cancellazione dell'account è irreversibile. I tuoi dati personali verranno eliminati, 
                    mentre i dati necessari per obblighi legali (es. fatture) verranno anonimizzati.
                  </p>
                </div>
              </div>

              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="request-deletion-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Richiedi Cancellazione Account
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog conferma cancellazione */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Conferma Cancellazione
            </DialogTitle>
            <DialogDescription>
              Stai per richiedere la cancellazione del tuo account. Un amministratore esaminerà 
              la richiesta entro 30 giorni.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">
                Motivo della richiesta (opzionale)
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Es. Non utilizzo più il servizio..."
                className="mt-2"
              />
            </div>

            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-2">Cosa succederà:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Il tuo account verrà disattivato</li>
                <li>I dati personali verranno eliminati</li>
                <li>Gli ordini storici verranno anonimizzati</li>
                <li>Non potrai più accedere alla piattaforma</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={requestDataDeletion}
              disabled={loading.deletion}
              data-testid="confirm-deletion-btn"
            >
              {loading.deletion && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Conferma Cancellazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrivacySettingsPage;
